import React, { Fragment, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { FormattedMessageWrappedInSpan } from '../components/misc';
import includes from 'lodash.includes';
import scrollToComponent from 'react-scroll-to-component';
import classNames from 'classnames';

import EventInfoCard from '../components/content/event/EventInfoCard';
import MediaList from '../components/content/lists/MediaList';
import PhotoBookInfoBanner from '../components/content/photo-book/PhotoBookInfoBanner';
import AccountDownloadTile from '../components/content/account/AccountDownloadTile';

import {
  includesPhotoBook,
  getVideo,
  includesVideo,
  includesFotoFlat,
} from '../utils/common';
import {
  ORIGINAL,
  SOCIAL_MEDIA,
  SINGLE_PHOTO,
  FOTO_FLAT,
  VIDEO,
  CERTIFICATE,
  VOUCHER,
  ALL_PHOTOS,
} from '../utils/variables';

const AccountEventPage = (props) => {
  const { intl, viewer } = props;
  const availableZips = viewer.account.eventPurchases.availableZips ?? [];
  const availableCertificates = viewer.account.eventPurchases.availableCertificates ?? [];
  const { account } = viewer;
  const restartState = account.eventPurchases.restartState;
  const originalsAreAvailable = includes(availableZips, ORIGINAL);
  const isFaceSearchEvent = viewer.account.boughtEvent.searchByFace;
  const hasVideo = includesVideo(viewer.account.boughtEvent.products);
  const hasBoughtFotoFlat =
    includesFotoFlat(viewer.account.boughtEvent.products) &&
    account.eventPurchases.availableZips.some(
      (value) => value !== ORIGINAL && value !== SOCIAL_MEDIA
    );
  const videoReadyForDownload = hasVideo
    ? getVideo(viewer.account.boughtEvent.products).price.amount == 0
    : null;
  const showPhotoDownload = availableZips?.length > 0;
  const hasCertificates = availableCertificates?.length > 0;
  const hasPhotoBook = includesPhotoBook(viewer.account.boughtEvent.products);
  const mediaListRef = useRef(null);

  const findEventVouchers = () => {
    const filteredOrders = account.orders?.edges.filter(
      (order) =>
        order.node.cart.lineItems.some(
          (item) => item.product.event.sgId === viewer.account.boughtEvent.sgId
        ) && order.node.vouchers.length > 0
    );
    return filteredOrders?.map((order) => ({
      vouchers: order.node.vouchers,
      orderid: order.node.orderid,
    }));
  };
  const vouchersForEvent = findEventVouchers();

  const handleCustomizePhotoBookClick = () => {
    const { history, match } = props;
    history.push(`/account/event/${match.params.id}/book`);
  };

  const scrollToFotoFlat = () => {
    scrollToComponent(mediaListRef.current, { align: 'top' });
  };

  return (
    <div className="container-960 container-page">
      <div className="account-event-header">
        <div className="row account-event-info-card">
          <EventInfoCard
            eventInfo={viewer.account.boughtEvent}
            eventBannerActAsLink={true}
            handleScrollToOffer={scrollToFotoFlat}
            shouldShowAdditionalOffer={false}
            startnumber={
              account.eventPurchases.participants[0] &&
              account.eventPurchases.participants[0].startnumber
            }
          />

          <div className=" col-xs-16 col-sm-6">
            <div className="account-download-container">
              <div className="account-download-body">
                <div className={classNames('container-label ml-15 mr-15 mb-15')}>
                  <FormattedMessageWrappedInSpan
                    id="accountEventPage.products.title"
                    defaultMessage="Download area"
                  />
                </div>
                <div className="account-download-btns">
                  {hasCertificates && hasBoughtFotoFlat && (
                    <AccountDownloadTile
                      type={CERTIFICATE}
                      onScrollToMedia={scrollToFotoFlat}
                      notActive={!originalsAreAvailable}
                    />
                  )}
                  <AccountDownloadTile
                    type={SINGLE_PHOTO}
                    onScrollToMedia={scrollToFotoFlat}
                  />
                  {showPhotoDownload && (
                    <AccountDownloadTile
                      eventSgId={viewer.account.boughtEvent.sgId}
                      type={ALL_PHOTOS}
                      values={availableZips}
                      restartState={viewer.account.eventPurchases.restartState}
                    />
                  )}
                  {hasBoughtFotoFlat && (
                    <AccountDownloadTile
                      type={FOTO_FLAT}
                      values={availableZips}
                      isFaceSearchEvent={isFaceSearchEvent}
                      eventSgId={viewer.account.boughtEvent.sgId}
                    />
                  )}
                  {hasVideo && hasBoughtFotoFlat && (
                    <AccountDownloadTile
                      values={account.eventPurchases.availableVideos}
                      type={VIDEO}
                      notActive={!videoReadyForDownload}
                      isFaceSearchEvent={isFaceSearchEvent}
                      eventSgId={viewer.account.boughtEvent.sgId}
                      intl={intl}
                    />
                  )}
                  {vouchersForEvent && vouchersForEvent.length > 0 && (
                    <AccountDownloadTile type={VOUCHER} values={[...vouchersForEvent]} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {hasPhotoBook && (
          <div className="row">
            <div className="col-xs-16 col-sm-12 col-sm-offset-2">
              <PhotoBookInfoBanner
                eventId={viewer.account.boughtEvent.sgId}
                eventName={viewer.account.boughtEvent.shortName}
                onContinueClick={handleCustomizePhotoBookClick}
              />
            </div>
          </div>
        )}
      </div>
      <div ref={mediaListRef}>
        {account.eventPurchases.participants.map((participant) => (
          <Fragment key={participant.startnumber}>
            <div className="row">
              <div className="col-xs-16 col-sm-12 col-sm-offset-2 account-media-header">
                <h1 className="separator">
                  {viewer.account.boughtEvent.searchByFace
                    ? intl.formatMessage(translations.boughtPhotosFaceEvent, {
                        count: participant.media.mediaInfo.count,
                      })
                    : intl.formatMessage(translations.boughtPhotos, {
                        count: participant.media.mediaInfo.count,
                        startNumber: participant.startnumber,
                      })}
                  {viewer.account.boughtEvent.searchByFace && (
                    <span>
                      {intl.formatMessage(translations.boughtPhotosFFVirtualId, {
                        virtualStartNumber: participant.startnumber,
                      })}
                    </span>
                  )}
                </h1>
              </div>
            </div>
            <MediaList
              media={participant.media.mediaList}
              offer={null}
              eventSgId={viewer.account.boughtEvent.sgId}
              startNumbers={account.eventPurchases.availableCertificates}
              listType="account"
              restartState={restartState}
              isFaceSearchEvent={isFaceSearchEvent}
              hasCertificates={hasCertificates}
            />
          </Fragment>
        ))}
      </div>
      {account.eventPurchases.singleMedia.mediaInfo.count > 0 && (
        <Fragment>
          <div className="row">
            <div className="col-xs-16 col-sm-12 col-sm-offset-2">
              <h1 className="separator">
                {intl.formatMessage(translations.singleMedia, {
                  count: account.eventPurchases.singleMedia.mediaInfo.count,
                })}
              </h1>
            </div>
          </div>
          <MediaList
            media={account.eventPurchases.singleMedia.mediaList}
            offer={null}
            eventSgId={viewer.account.boughtEvent.sgId}
            startNumbers={account.eventPurchases.availableCertificates}
            listType="account"
            restartState={restartState}
            isFaceSearchEvent={isFaceSearchEvent}
            hasCertificates={hasCertificates}
          />
        </Fragment>
      )}

      {account.eventPurchases.bonusMedia.mediaInfo.count > 0 && (
        <Fragment>
          <div className="row">
            <div className="col-xs-16 col-sm-12 col-sm-offset-2">
              <h1 className="separator">
                {intl.formatMessage(translations.freePhotos, {
                  count: account.eventPurchases.bonusMedia.mediaInfo.count,
                })}
              </h1>
            </div>
          </div>
          <MediaList
            media={account.eventPurchases.bonusMedia.mediaList}
            eventSgId={viewer.account.boughtEvent.sgId}
            offer={null}
            listType="account"
            restartState={account.eventPurchases.restartState}
          />
        </Fragment>
      )}
    </div>
  );
};

const translations = defineMessages({
  boughtPhotos: {
    id: 'accountEventPage.boughtPhotos',
    defaultMessage:
      'Listing {count, plural, one {# photo} other {# photos}} for bib number {startNumber}',
  },
  boughtPhotosFaceEvent: {
    id: 'accountEventPage.boughtPhotosFaceEvent',
    defaultMessage: 'Listing {count, plural, one {# photo} other {# photos}} you bought',
  },
  boughtPhotosFFVirtualId: {
    id: 'accountEventPage.boughtPhotosFFVirtualId',
    defaultMessage: 'Foto-Flat ID {virtualStartNumber}',
  },
  singleMedia: {
    id: 'accountEventPage.singleMedia',
    defaultMessage:
      'Listing {count, plural, one {# photo} other {# photos}} you bought for this event',
  },
  freePhotos: {
    id: 'accountEventPage.freePhotos',
    defaultMessage:
      'Listing {count, plural, one {# free photo} other {# free photos}} for this event',
  },
  requestVideoButton: {
    id: 'accountEventPage.requestVideoButton',
    defaultMessage: 'Generate video for bib number {startNumber}',
  },
  downloadVideoButton: {
    id: 'accountEventPage.downloadVideoButton',
    defaultMessage: 'Download video for bib number {startNumber}',
  },
});

AccountEventPage.propTypes = {
  intl: PropTypes.object,
  viewer: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
};

export { AccountEventPage };

export default createFragmentContainer(injectIntl(AccountEventPage), {
  viewer: graphql`
    fragment AccountEventPage_viewer on Viewer {
      account {
        orders(first: 100) {
          edges {
            node {
              id
              cart {
                lineItems {
                  product {
                    event {
                      name
                      sgId
                    }
                  }
                }
              }
              orderid
              vouchers {
                id
                code
                campaign {
                  id
                  banner
                  url
                }
              }
            }
          }
        }
        boughtEvent(eventId: $eventId) {
          name
          products {
            type
            price
          }
          sgId
          shortName
          searchByFace
          ...EventInfoCard_eventInfo
        }
        eventPurchases(byStartnumber: true, eventId: $eventId) {
          availableCertificates
          availableVideos
          availableZips
          bonusMedia {
            mediaInfo {
              count
            }
            mediaList(first: 1000) {
              edges {
                node {
                  id
                  sgId
                  variants {
                    format
                    name
                    url
                  }
                }
              }
              ...MediaList_media
            }
          }
          canRestart
          restartState
          participants {
            media {
              mediaInfo {
                count
              }
              mediaList(first: 1000) {
                edges {
                  node {
                    id
                    sgId
                    variants {
                      format
                      name
                      url
                    }
                  }
                }
                ...MediaList_media
              }
            }
            startnumber
          }
          singleMedia {
            mediaInfo {
              count
            }
            mediaList(first: 1000) {
              edges {
                node {
                  id
                  sgId
                  variants {
                    format
                    name
                    url
                  }
                }
              }
              ...MediaList_media
            }
          }
        }
      }
    }
  `,
});
